/**
 * ░██████╗░██╗████████╗██╗░░██╗██╗░░░██╗██████╗░  ██╗
 * ██╔════╝░██║╚══██╔══╝██║░░██║██║░░░██║██╔══██╗  ╚═╝
 * ██║░░██╗░██║░░░██║░░░███████║██║░░░██║██████╦╝  ░░░
 * ██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░██║██╔══██╗  ░░░
 * ╚██████╔╝██║░░░██║░░░██║░░██║╚██████╔╝██████╦╝  ██╗
 * ░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝░╚═════╝░╚═════╝░  ╚═╝
 * 
 * ███╗░░██╗░█████╗░████████╗██╗░░██╗░█████╗░███╗░░██╗███████╗██╗░░██╗░█████╗░
 * ████╗░██║██╔══██╗╚══██╔══╝██║░██╔╝██╔══██╗████╗░██║██╔════╝██║░██╔╝██╔══██╗
 * ██╔██╗██║██║░░██║░░░██║░░░█████═╝░██║░░██║██╔██╗██║█████╗░░█████═╝░██║░░██║
 * ██║╚████║██║░░██║░░░██║░░░██╔═██╗░██║░░██║██║╚████║██╔══╝░░██╔═██╗░██║░░██║
 * ██║░╚███║╚█████╔╝░░░██║░░░██║░╚██╗╚█████╔╝██║░╚███║███████╗██║░╚██╗╚█████╔╝
 * ╚═╝░░╚══╝░╚════╝░░░░╚═╝░░░╚═╝░░╚═╝░╚════╝░╚═╝░░╚══╝╚══════╝╚═╝░░╚═╝░╚════╝░
 */

import { useEffect } from 'react';

const BASE_URL = 'https://portfolio.notkoneko.space';
const DEFAULT_IMAGE = `${BASE_URL}/lyrika-avatar.webp`;
const DEFAULT_TITLE = 'NotKoneko — CS, Neuroscience & Psychology';
const DEFAULT_DESCRIPTION = 'Architecting predictive data models, specialized API engines, and native mobile applications at the intersection of human cognition and machine intelligence.';
const DEFAULT_KEYWORDS = 'Koneko, NotKoneko, NottKoneko, Computer Science, Neuroscience, Psychology, Arizona State University, NeuroSyn, Lyrika-Stanza, Swift 6, Discord Bots, Machine Learning, Bioacoustics, Software Engineer, Cognitive Researcher';

/**
 * Helper to update or create a <meta> or <link> tag in document.head
 */
const setMetaTag = (attr, key, content) => {
  if (!content) return;
  let element = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setLinkTag = (rel, href) => {
  if (!href) return;
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

/**
 * Dynamic SEO component managing document head, social previews, canonical tags, and JSON-LD schema
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  keywords,
  image = DEFAULT_IMAGE,
  type = 'website',
  schema = null,
  noindex = false,
  customDomain = null,
}) => {
  useEffect(() => {
    // 1. Calculate Title & URLs
    const formattedTitle = title
      ? (title.includes('NotKoneko') ? title : `${title} | NotKoneko`)
      : DEFAULT_TITLE;

    document.title = formattedTitle;

    const domain = customDomain || BASE_URL;
    const cleanPath = path ? (path.startsWith('/') ? path : `/${path}`) : '';
    const canonicalUrl = `${domain}${cleanPath}`;
    const fullImageUrl = image.startsWith('http') ? image : `${BASE_URL}${image.startsWith('/') ? image : `/${image}`}`;

    const keywordString = Array.isArray(keywords)
      ? `${keywords.join(', ')}, ${DEFAULT_KEYWORDS}`
      : (keywords ? `${keywords}, ${DEFAULT_KEYWORDS}` : DEFAULT_KEYWORDS);

    // 2. Standard Metadata
    setMetaTag('name', 'description', description);
    setMetaTag('name', 'keywords', keywordString);
    setMetaTag('name', 'author', 'NotKoneko');
    setMetaTag('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // 3. Canonical URL
    setLinkTag('canonical', canonicalUrl);

    // 4. OpenGraph Tags
    setMetaTag('property', 'og:title', formattedTitle);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:image', fullImageUrl);
    setMetaTag('property', 'og:type', type);
    setMetaTag('property', 'og:site_name', 'NotKoneko Portfolio');
    setMetaTag('property', 'og:locale', 'en_US');

    // 5. Twitter Card Tags
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:site', '@NottKoneko');
    setMetaTag('name', 'twitter:creator', '@NottKoneko');
    setMetaTag('name', 'twitter:title', formattedTitle);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', fullImageUrl);

    // 6. Structured Data (JSON-LD)
    let schemaScript = document.head.querySelector('#schema-jsonld');
    if (schema) {
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'schema-jsonld';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    } else if (schemaScript) {
      schemaScript.remove();
    }
  }, [title, description, path, keywords, image, type, schema, noindex, customDomain]);

  return null;
};

export default SEO;
